//Components/BrokerTracker.js

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDownNarrowWide, ArrowUpNarrowWide } from "lucide-react";
import { formatExpectedTiming } from "../utils/timeFormat";
import { PROJECT_STATUSES, STATUS_TONE, getStatusSortIndex, normalizeProjectStatus } from "../utils/projectStatus";

export default function BrokerTracker({
  projects = [],
  loading = false,
  limitDays = 30,
  maxVisibleRows = 5,
  enableSorting = true,
}) {
  const navigate = useNavigate();

  // ----- brand -----
  const BRAND_RED = "#EB040D";
  const PRIORITY_BG = {
    Urgent: "rgba(211, 34, 43, 0.80)",
    Medium: "rgba(63, 63, 63, 0.80)",
    Low: "rgba(131, 131, 132, 0.80)",
  };

  // ----- sorting -----
  const [sort, setSort] = useState({ by: "modified", dir: "desc" });
  const STATUS_ORDER = PROJECT_STATUSES;
  const PRIORITY_ORDER = ["Urgent", "Medium", "Low"];
  const toggleSort = (by) => {
    if (!enableSorting) return;
    setSort((p) => (p.by === by ? { by, dir: p.dir === "asc" ? "desc" : "asc" } : { by, dir: "asc" }));
  };

  // ----- helpers -----
  const cutoff = useMemo(() => {
    if (!limitDays) return null;
    const d = new Date();
    d.setDate(d.getDate() - limitDays);
    return d;
  }, [limitDays]);

  const normalizeStatus = (s) => normalizeProjectStatus(s);

  const getModifiedTime = (row) =>
    new Date(row?.updated_at || row?.broker_intake?.created_at || 0).getTime();

  // Filter by recent modifications, backfill if needed, then apply visible sort.
  const rows = useMemo(() => {
    const base = Array.isArray(projects) ? projects : [];

    const byNewest = [...base].sort((a, b) => {
      return getModifiedTime(b) - getModifiedTime(a);
    });

    const inWindow = byNewest.filter((r) => {
      if (!cutoff) return true;
      const ts = r?.updated_at || r?.broker_intake?.created_at;
      return ts ? new Date(ts) >= cutoff : false;
    });

    const need = Math.max(0, maxVisibleRows - inWindow.length);
    const backfill =
      need > 0 ? byNewest.filter((r) => !inWindow.includes(r)).slice(0, need) : [];

    const combined = [...inWindow, ...backfill];

    const dirMul = sort.dir === "asc" ? 1 : -1;
    return combined.sort((a, b) => {
      if (sort.by === "tail") {
        const A = (a.tail_number || "").toString();
        const B = (b.tail_number || "").toString();
        return A.localeCompare(B, undefined, { numeric: true, sensitivity: "base" }) * dirMul;
      }
      if (sort.by === "status") {
        return (getStatusSortIndex(a.current_status) - getStatusSortIndex(b.current_status)) * dirMul;
      }
      if (sort.by === "priority") {
        const A = PRIORITY_ORDER.indexOf(a.priority || "");
        const B = PRIORITY_ORDER.indexOf(b.priority || "");
        return (A - B) * dirMul;
      }
      return (getModifiedTime(a) - getModifiedTime(b)) * dirMul;
    }).slice(0, maxVisibleRows);
  }, [projects, cutoff, sort, maxVisibleRows]);

  // viewport height: header (~52) + row (64) * N
  const ROW_H = 64;
  const maxBodyHeight = 52 + Math.max(1, maxVisibleRows) * ROW_H;

  // ----- tiny UI bits -----
  const Th = ({ id, label }) => {
    const active = sort.by === id;
    const icon = !enableSorting
      ? null
      : active
      ? sort.dir === "asc"
        ? <ArrowUpNarrowWide className="h-4 w-4" />
        : <ArrowDownNarrowWide className="h-4 w-4" />
      : <ArrowDownNarrowWide className="h-4 w-4 opacity-40" />;

    return (
      <th className="py-3 px-3 sm:px-4 font-medium border-r border-black/10 last:border-r-0">
        <button
          type="button"
          onClick={() => toggleSort(id)}
          className={`inline-flex items-center gap-1.5 group ${enableSorting ? "cursor-pointer" : "cursor-default"}`}
        >
          <span className="text-[12px] sm:text-xs tracking-wide">{label}</span>
          {enableSorting && <span className="opacity-70 group-hover:opacity-100 transition">{icon}</span>}
        </button>
      </th>
    );
  };

  const StatusPill = ({ status }) => {
    const s = normalizeStatus(status);
    const tone = STATUS_TONE[s] || "rgba(0,0,0,0.25)";
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-medium"
        style={{ color: tone, background: "rgba(0,0,0,0.035)", boxShadow: `inset 0 0 0 1px ${tone}22` }}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone }} />
        {s || "—"}
      </span>
    );
  };

  const PriorityPill = ({ priority }) => {
    const bg = PRIORITY_BG[priority] || "rgba(0,0,0,0.06)";
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-semibold"
        style={{ background: bg, color: '#FFFFFF' }}
      >
        {priority || "—"}
      </span>
    );
  };

  return (
  <div className="text-sm text-[#1A1A1A]">
    {/* Outer shell: border + rounded, but we're going to split header (gray) and body (white) */}
    <div className="rounded-xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
         style={{ border: "1px solid rgba(0,0,0,0.06)" }}>

      {/* ===== Gray header band ===== */}
      <div
        className="
          bg-grey backdrop-blur   /* soft gray, tweak to taste */
          backdrop-blur-sm
          px-3 sm:px-4 py-3
          flex items-end justify-between
        "
      >
        <div>
          <h2 className="text-lg font-semibold">My Project Tracker</h2>
          <p className="text-[11px] sm:text-xs text-black/55 mt-0.5">
  Showing 5 last modified projects • sorted by{" "}
  <span className="font-medium">{sort.by === "modified" ? "last modified" : sort.by}</span> ({sort.dir})
</p>

        </div>

        {/* desktop "View all" in the header band */}
        <Link
          to="/brokerprojects"
          className="hidden sm:inline text-xs font-medium text-[#1A1A1A]/70 hover:text-[#1A1A1A] transition underline underline-offset-4"
        >
          View all →
        </Link>
      </div>

      {/* ===== White table block (edge-to-edge) ===== */}
      <div className="bg-white">
        {/* sticky head, scroll body; height target ≈ header(52) + row(64)*N */}
        <div className="overflow-auto" style={{ maxHeight: maxBodyHeight }}>
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-black/10 text-black/60">
                <Th id="tail" label="Tail #" />
<Th id="priority" label="Priority" />
<Th id="status" label="Status" />
<th className="py-3 px-3 sm:px-4 font-medium border-r border-black/10 last:border-r-0">
  <span className="text-[12px] sm:text-xs tracking-wide">Expected Timing</span>
</th>
<th className="py-3 px-3 sm:px-4 font-medium">
  <span className="text-[12px] sm:text-xs tracking-wide">Services</span>
</th>

              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="py-6 px-4 text-center text-black/50 border-b border-black/10">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 px-4 text-center text-black/40 border-b border-black/10">
                    No projects found.
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((p) => {
                  const detailPath = `/projects/${encodeURIComponent(p.tail_number || "")}`;
                  return (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    onClick={() => p.tail_number && navigate(detailPath)}
                    onKeyDown={(event) => {
                      if ((event.key === "Enter" || event.key === " ") && p.tail_number) {
                        event.preventDefault();
                        navigate(detailPath);
                      }
                    }}
                    className="
                      border-b border-black/5       /* <— ROW divider. Remove this delete row lines */
                      hover:bg-black/[0.02] transition cursor-pointer focus:outline-none focus:bg-black/[0.03]
                    "
                  >
                    <td className="py-4 px-3 sm:px-4 font-medium border-r border-black/10">{p.tail_number || "—"}</td>
<td className="py-4 px-3 sm:px-4 border-r border-black/10"><PriorityPill priority={p.priority} /></td>
<td className="py-4 px-3 sm:px-4 border-r border-black/10"><StatusPill status={p.current_status} /></td>


{/* NEW: Expected Timing column */}
<td className="py-4 px-3 sm:px-4 border-r border-black/10">
  {formatExpectedTiming(p.expected_datetime, p.timezone)}
</td>

<td className="py-4 px-3 sm:px-4">{p.broker_intake?.services || "—"}</td>

                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* mobile "View all" at bottom */}
        <div className="sm:hidden flex justify-end p-3 border-t border-black/10 bg-white">
          <Link
            to="/brokerprojects"
            className="text-xs font-medium text-[#1A1A1A]/70 hover:text-[#1A1A1A] transition underline underline-offset-4"
          >
            View all →
          </Link>
        </div>
      </div>
    </div>
  </div>
);
}
